/**
 * US-018 AC1/AC2 — desktop-only native notification watcher.
 *
 * Web-safe: every effect below no-ops when `desktop` is undefined. See
 * DesktopAppEvents.tsx for how this is conditionally mounted (a separate
 * inner component, only rendered inside the Electron shell) rather than
 * gated by an early return in a component that unconditionally calls
 * these same data hooks — Rules of Hooks.
 *
 * Polling: invalidates the three underlying query keys on an interval,
 * mirroring useShiftMonitor.ts's established pattern, rather than passing
 * a `refetchInterval` into useMyTasks/useMedicationsDue/useIncidents
 * directly — those hooks are used elsewhere in the app (dashboards, list
 * pages) and baking a refetchInterval into the hook itself would add
 * unwanted background polling to every existing caller, not just this
 * watcher.
 *
 * Diffing: each source tracks the previously-seen ID set in a ref and
 * only notifies for IDs that are new since the LAST successful fetch —
 * never on first load (would spam a notification for every already-open
 * item the moment the app starts) and never for an item that was already
 * seen and is just still present on a later poll.
 *
 * Trigger scope (product decision, 2026-07-07): new incident reported,
 * medication overdue, task/form overdue. "Shift starting soon" was
 * considered and explicitly NOT selected.
 *
 * Incidents note: this domain model has no per-incident "assigned to"
 * concept (see types/incident.ts — only reporter/resolver, no assignee
 * field) — "new incident reported" is filtered to status "reported"
 * (freshly filed, not yet triaged) rather than a literal "assigned to
 * you", which doesn't exist here.
 *
 * HIPAA: notification bodies are always generic counts/text, never a
 * resident's name, medication name, or incident detail — this is
 * OS-level UI, potentially visible on a lock screen. See platform.ts's
 * showNotification() JSDoc for the same constraint at the bridge
 * boundary.
 *
 * Dock badge (AC4): a separate concern from the diff-based notifications
 * above — the badge always reflects the CURRENT total pending count
 * (overdue tasks + overdue meds + reported incidents), not "how many are
 * new since last check". Recomputed independently of the diff refs on
 * every data update.
 */
import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { desktop } from "@/lib/platform";
import { useAuth } from "./useAuth";
import { useMyTasks } from "./useMyTasks";
import { useMedicationsDue } from "./useMedications";
import { useIncidents } from "./useIncidents";
import { useFacilityContext } from "./useFacilityContext";

const POLL_INTERVAL_MS = 30_000;

export function useDesktopNotificationWatcher(): void {
  const isDesktop = desktop !== undefined;
  const queryClient = useQueryClient();
  // GET /api/me/tasks 403s outright with no employee record — "platform
  // admins, external auditors" per auth.ts's own comment — and that 403 is
  // NOT logged server-side (a raw NextResponse.json, no logger call), so it
  // was invisible until confirmed via the global query-error toast firing
  // every 30s for such an account signed into a native shell. Every OTHER
  // query this watcher polls (medications due, incidents) works fine without
  // an employee record; only my-tasks structurally requires one. Fixed
  // 2026-09-13: skip the my-tasks query entirely rather than fire it
  // unconditionally for every native-shell user regardless of account type.
  const { data: authContext } = useAuth();
  const hasEmployeeRecord = !!authContext?.employeeId;

  // GET /api/facilities/:id/medications/due enforces requireOnShift
  // server-side (US-052) for every employee, unconditionally — unlike
  // my-tasks' no-employee-record exemption above, this one has no exemption
  // for account type at all. An employee account that is simply between
  // shifts (confirmed via a real account's shift history, 2026-09-15) gets a
  // legitimate 403 here, which this watcher was firing unconditionally for
  // every native-shell user regardless of shift state — the second, separate
  // cause of the same intermittent "access denied" notification toast as the
  // my-tasks fix above. Gated the same way: skip the query entirely rather
  // than let it fire and 403.
  const { isOnShift } = useFacilityContext();

  const { data: tasksData } = useMyTasks(isDesktop && hasEmployeeRecord);
  const { data: medsData } = useMedicationsDue(
    undefined,
    undefined,
    isDesktop && isOnShift,
  );
  const { data: incidentsData } = useIncidents({ status: "reported" });

  const seenTaskIdsRef = useRef<Set<string> | null>(null);
  const seenMedIdsRef = useRef<Set<string> | null>(null);
  const seenIncidentIdsRef = useRef<Set<string> | null>(null);

  useEffect(() => {
    if (!isDesktop) return;
    const id = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ["my-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["medicationsDue"] });
      queryClient.invalidateQueries({ queryKey: ["incidents"] });
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isDesktop, queryClient]);

  useEffect(() => {
    if (!desktop || !tasksData) return;
    const overdueIds = new Set(
      tasksData.tasks.filter((t) => t.status === "overdue").map((t) => t.id),
    );
    const previouslySeen = seenTaskIdsRef.current;
    seenTaskIdsRef.current = overdueIds;
    if (!previouslySeen) return; // first observation — record, don't notify
    const newCount = [...overdueIds].filter(
      (id) => !previouslySeen.has(id),
    ).length;
    if (newCount > 0) {
      desktop.showNotification({
        title: "Task overdue",
        body:
          newCount === 1
            ? "A task is overdue."
            : `${newCount} tasks are overdue.`,
        route: "/my-tasks",
      });
    }
  }, [tasksData]);

  useEffect(() => {
    if (!desktop || !medsData) return;
    const overdueIds = new Set(
      medsData.flatMap((group) =>
        group.medications
          .filter((m) => m.status === "overdue")
          .map((m) => m.administration_id),
      ),
    );
    const previouslySeen = seenMedIdsRef.current;
    seenMedIdsRef.current = overdueIds;
    if (!previouslySeen) return;
    const newCount = [...overdueIds].filter(
      (id) => !previouslySeen.has(id),
    ).length;
    if (newCount > 0) {
      desktop.showNotification({
        title: "Medication due",
        body:
          newCount === 1
            ? "A medication is overdue."
            : `${newCount} medications are overdue.`,
        route: "/medications-due",
      });
    }
  }, [medsData]);

  useEffect(() => {
    if (!desktop || !incidentsData) return;
    const ids = new Set(incidentsData.incidents.map((i) => i.id));
    const previouslySeen = seenIncidentIdsRef.current;
    seenIncidentIdsRef.current = ids;
    if (!previouslySeen) return;
    const newCount = [...ids].filter((id) => !previouslySeen.has(id)).length;
    if (newCount > 0) {
      desktop.showNotification({
        title: "New incident reported",
        body:
          newCount === 1
            ? "Open LivingSyncs to view."
            : `${newCount} new incidents reported.`,
        route: "/incidents",
      });
    }
  }, [incidentsData]);

  useEffect(() => {
    if (!desktop) return;
    const overdueTasks =
      tasksData?.tasks.filter((t) => t.status === "overdue").length ?? 0;
    const overdueMeds =
      medsData?.reduce(
        (sum, group) =>
          sum + group.medications.filter((m) => m.status === "overdue").length,
        0,
      ) ?? 0;
    // .total (server-reported count across all pages), not
    // incidentsData.incidents.length (just the current page, now that
    // /api/incidents is properly paginated) -- see useIncidents.ts.
    const reportedIncidents = incidentsData?.total ?? 0;
    desktop.setBadgeCount(overdueTasks + overdueMeds + reportedIncidents);
  }, [tasksData, medsData, incidentsData]);
}
